import { HttpClient, HttpEvent, HttpEventType, HttpParams, HttpResponse } from "@angular/common/http";
import { EventEmitter, inject, Injectable } from "@angular/core";
import { firstValueFrom, last, Observable, Subscriber, tap } from "rxjs";
import { deserialize, HomePage, HomePageFeature } from "src/app/common/models/home-page.model";
import { AbstractService } from "src/app/common/services/abstract.service";
import { ConfigService } from "src/app/common/services/config.service";
import { LanguageService } from "src/app/common/services/language.service";
import { SegmentSpeakerService } from "src/app/common/services/segment-speaker.service";
import { TimedCache } from "src/app/common/tools/TimedCache";
import { SpeechSegment } from "src/app/modules/speech2text/models/speech-segment.model";

const API_PATH = "/api/internal/homepage";

@Injectable({
  providedIn: "root",
})
export class HomePageService extends AbstractService implements SegmentSpeakerService<HomePageFeature> {
  private readonly httpClient = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly languageService = inject(LanguageService);

  private featureCache = new TimedCache(3600 * 1000, async () => await this.getFeaturesByLanguage());
  private readonly featureUpdated = new EventEmitter<HomePageFeature>();

  async getFeatures(): Promise<HomePageFeature[]> {
    return await this.featureCache.getValue();
  }

  async getHomePage(): Promise<HomePage> {
    return await firstValueFrom(this.httpClient.get<HomePage>(API_PATH));
  }

  async update(homePage: HomePage): Promise<HomePage> {
    return await firstValueFrom(
      this.httpClient.put<HomePage>(API_PATH, JSON.stringify(homePage), {
        headers: { "Content-Type": "application/json" },
      }),
    );
  }

  async createFeature(title: string, description: string, videoUrl: string): Promise<HomePageFeature> {
    const res = await firstValueFrom(
      this.httpClient.post<HomePageFeature>(`${API_PATH}/feature`, {
        title,
        description,
        videoUrl,
        language: this.languageService.getCurrentLanguage(),
      }),
    );
    this.evictAndRefreshCash();
    return res;
  }

  async updateFeature(feature: HomePageFeature): Promise<void> {
    await firstValueFrom(this.httpClient.put(`${API_PATH}/feature`, feature));
    this.evictAndRefreshCash(); //TODO still needed ?
  }

  async deleteFeature(feature: HomePageFeature): Promise<HomePage> {
    const res = await firstValueFrom(this.httpClient.delete<HomePage>(`${API_PATH}/feature/${feature.id}`));
    this.evictAndRefreshCash();
    return res;
  }

  uploadToS3(file: File): Observable<HttpEvent<string>> {
    return new Observable<HttpEvent<string>>((subscriber) => {
      void this.pushToS3(file, subscriber);
    });
  }

  private async pushToS3(file: File, subscriber: Subscriber<HttpEvent<string>>): Promise<void> {
    const extension = file.name.split(".").pop();
    const id = crypto.randomUUID();
    const filepath = `${id}.${extension}`;
    const uploadUrl = await this.getUploadUrl(filepath);
    // Perform the request and handle the response
    this.httpClient
      .request("PUT", uploadUrl, { body: file, reportProgress: true, responseType: "blob", observe: "events" })
      .pipe(
        tap((event: HttpEvent<Blob>) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            subscriber.next({ type: HttpEventType.UploadProgress, loaded: event.loaded, total: event.total });
          }
        }),
        last(),
      )
      .subscribe({
        next: () => {
          subscriber.next(new HttpResponse<string>({ body: filepath }));
          subscriber.complete();
        },
        error: (err: unknown) => {
          subscriber.error(err);
        },
      });
  }

  async getUploadUrl(filename: string): Promise<string> {
    const params = new HttpParams().appendAll({ keyName: filename });
    return await firstValueFrom(this.httpClient.get<string>(`${API_PATH}/presigned-upload-url`, { params }));
  }

  async getFeaturesByLanguage(): Promise<HomePageFeature[]> {
    const currentLanguage = this.languageService.getCurrentLanguage();
    return await firstValueFrom(this.httpClient.get<HomePageFeature[]>(`${API_PATH}/features/${currentLanguage}`));
  }

  private evictAndRefreshCash(): void {
    this.featureCache = new TimedCache(3600 * 1000, async () => await this.getFeaturesByLanguage());
  }

  async getFeature(featureId: string): Promise<HomePageFeature> {
    return await firstValueFrom(this.httpClient.get<HomePageFeature>(`${API_PATH}/feature/${featureId}`));
  }

  getAwsCloudFrontDistributionUrl(): string {
    return this.configService.cloudfrontUrl;
  }

  async setSegmentSpeaker(featureId: string, speakerId: string, speakerEntityId?: string): Promise<HomePageFeature> {
    const url = `${API_PATH}/${featureId}/speaker/${speakerId}`;
    const result = deserialize(await firstValueFrom(this.httpClient.put<HomePageFeature>(url, speakerEntityId)));
    this.onUpdate(result);
    return result;
  }

  async removeSegmentSpeaker(featureId: string, speakerId: string): Promise<HomePageFeature> {
    const url = `${API_PATH}/${featureId}/speaker/${speakerId}`;
    const result = deserialize(await firstValueFrom(this.httpClient.delete<HomePageFeature>(url)));
    this.onUpdate(result);
    return result;
  }

  async saveSegments(id: string, start: number, texts: string[], language?: string): Promise<SpeechSegment[]> {
    const url = `${API_PATH}/${id}/segments/${language || "-"}/${start}`;
    return await firstValueFrom(this.httpClient.put<SpeechSegment[]>(url, texts));
  }

  async generateInstructions(id: string): Promise<void> {
    const homePageFeature = deserialize(
      await firstValueFrom(this.httpClient.put<HomePageFeature>(`${API_PATH}/feature/${id}/generate-instructions`, {})),
    );
    this.onUpdate(homePageFeature);
  }

  onUpdate(update: HomePageFeature): void {
    this.featureUpdated.emit(update);
  }

  getUpdates(): Observable<HomePageFeature> {
    return this.featureUpdated;
  }
}
